/**
 * Список чайных филиала (эталон screen-16, секция teas): ряд «название ·
 * ₽цена (0 → серый пунктир) · вместимость · ✕ 20×20», тап по ряду —
 * inline-редактор (имя + две кнопки-значения → NumPad money/int max 30).
 *
 * ПРИНЦИП эталона (CFG:103): «закрытие ряда = сохранение; пустое имя =
 * отмена». Enter или pointerdown вне ряда (capture, регистрация через
 * setTimeout(0)) коммитит; тапы внутри ряда, внутри NumPad-портала и внутри
 * модалок ряд НЕ закрывают; Esc — явная отмена. «+ Добавить» — dashed-green
 * строка с автофокусом имени. Удаление — БЕЗ модалки (A43.2): строка сразу
 * прячется локальным pending-набором (фоновый reload её не воскресит —
 * фильтр живёт в компоненте), снекбар «Чайная «X» удалена · ОТМЕНИТЬ»;
 * DELETE отложен — уходит по истечении 5с/вытеснении/уходе с экрана
 * (unmount), «Отменить» возвращает строку без сети. Пока открыт ряд —
 * экран dirty (onDirty), гарды у родителя.
 */
import { useEffect, useRef, useState, type SVGProps } from "react";
import { NumPad, useUndoSnackbar } from "../../components/shared";
import type { TeaRoom } from "../../api/types";
import { isPhantomTea, type TeaDraft } from "../../hooks/useBranchConfig";
import css from "./config.module.css";

const NB = " ";
/** ₽ + группировка тысяч NBSP (эталон CFG:6). */
export const money = (n: number) => `₽${String(Math.round(n || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, NB)}`;

type P = SVGProps<SVGSVGElement>;
const Cup = (p: P) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} {...p}>
    <path d="M5 9h11v6a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4z" />
    <path d="M16 10h3.5v2.5a3 3 0 0 1-3 3H16" />
    <path d="M8 6V4M12 6V4" />
  </svg>
);
const Cap = (p: P) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} {...p}>
    <path d="M9 4h6v5H9z" />
    <path d="M5 20v-4l3-3h8l3 3v4z" />
  </svg>
);
const Cross = (p: P) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} {...p}>
    <path d="M5 5l14 14M19 5L5 19" />
  </svg>
);
const Plus = (p: P) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} {...p}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export interface EoConfigTeasProps {
  branchId: string;
  teas: TeaRoom[];
  /** flashOk из useBranchConfig: ключ `tea:<id>` + счётчик перезапуска. */
  flash: { key: string; n: number } | null;
  onDirty: (d: boolean) => void;
  saveTea: (id: string | null, draft: TeaDraft) => void;
  deleteTea: (id: string) => void;
}

export function EoConfigTeas({ branchId, teas, flash, onDirty, saveTea, deleteTea }: EoConfigTeasProps) {
  /* row: null — все ряды закрыты; { id: null } — открыта строка добавления. */
  const [row, setRow] = useState<{ id: string | null } | null>(null);
  const [draft, setDraft] = useState<TeaDraft>({ name: "", price: 0, capacity: 0 });
  const [np, setNp] = useState<"price" | "cap" | null>(null);
  /* Скрытые ряды с отложенным DELETE: id живёт тут до onExpire/onUndo. */
  const [pending, setPending] = useState<string[]>([]);
  const snack = useUndoSnackbar();
  const rowRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const npRef = useRef(false);
  npRef.current = np !== null;

  /* pending НЕ сбрасываем: отложенный DELETE чужого филиала так же валиден. */
  useEffect(() => { setRow(null); setNp(null); }, [branchId]);
  useEffect(() => { onDirty(row !== null); }, [row, onDirty]);
  useEffect(() => { if (row) nameRef.current?.focus(); }, [row]);

  /* Закрытие = подтверждение; пустое имя = отмена (эталон CFG:104). */
  const finish = () => {
    const name = draft.name.trim();
    if (name && row) saveTea(row.id, { name, price: draft.price, capacity: draft.capacity });
    setRow(null);
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  /* Тап вне ряда — commit. Capture-фаза; подписка через setTimeout(0), чтобы
     открывший ряд тап не закрыл его сразу же (эталон CFG:105-106).
     Не закрывают ряд: тапы внутри ряда, NumPad поверх, модалки и контролы
     смены филиала/суб-таба ([data-cfg-guard] — там решает confirm-модалка). */
  useEffect(() => {
    if (!row) return;
    const out = (e: PointerEvent) => {
      if (npRef.current) return; // NumPad открыт поверх — его тапы ряд не трогают
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (rowRef.current?.contains(t) || t.closest("[data-cfg-modal],[data-cfg-guard]")) return;
      finishRef.current();
    };
    const timer = window.setTimeout(() => document.addEventListener("pointerdown", out, true), 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("pointerdown", out, true);
    };
  }, [row]);

  const openEdit = (t: TeaRoom) => {
    /* Фантом (POST в полёте): правка ушла бы PUT'ом на временный id —
       сервер «примет» 0 строк, а значение молча потеряется. Ряд ждёт id. */
    if (isPhantomTea(t.id)) return;
    setRow({ id: t.id });
    setDraft({ name: t.name, price: t.price, capacity: t.capacity });
  };
  const openAdd = () => {
    if (row?.id === null) { nameRef.current?.focus(); return; } // уже открыта — рефокус
    setRow({ id: null });
    setDraft({ name: "", price: 0, capacity: 0 });
  };

  /* A43.2: строка сразу прячется, DELETE отложен на снекбар. «Отменить» —
     вернуть без сети; истечение/вытеснение/unmount — pending снимается И
     уходит deleteTea (его optimistic-слой прячет ряд дальше, а при ошибке
     сети честно возвращает строку с красным hint). */
  const requestDelete = (t: TeaRoom) => {
    if (isPhantomTea(t.id)) return;
    setPending((p) => [...p, t.id]);
    if (row?.id === t.id) setRow(null);
    snack.show({
      label: `Чайная «${t.name}» удалена`,
      onUndo: () => setPending((p) => p.filter((x) => x !== t.id)),
      onExpire: () => {
        setPending((p) => p.filter((x) => x !== t.id));
        deleteTea(t.id);
      },
    });
  };

  const editor = (
    <div className={css.teaEdit} ref={rowRef}>
      <input
        ref={nameRef}
        className={css.teName}
        type="text"
        placeholder="Название"
        value={draft.name}
        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
        onKeyDown={(e) => {
          if (e.key === "Enter") finish();
          else if (e.key === "Escape") setRow(null); /* явная отмена */
        }}
      />
      <button type="button" className={css.teNumbtn} title="Цена" onClick={() => setNp("price")}>
        {draft.price ? money(draft.price) : <span className={css.dash}>—</span>}
      </button>
      <button type="button" className={css.teNumbtn} title="Вместимость" onClick={() => setNp("cap")}>
        <Cap />
        {draft.capacity ? String(draft.capacity) : <span className={css.dash}>—</span>}
      </button>
    </div>
  );

  /* Видимые ряды: pending-набор прячет удалённые до исхода снекбара. */
  const shown = teas.filter((t) => !pending.includes(t.id));

  return (
    <section className={css.teasec}>
      <div className={css.tsecH}>
        <span className={css.cup}><Cup /></span>
        <h2 className={css.tsecT}>Чайные ({shown.length})</h2>
        <button type="button" className={css.btnAdd} onClick={openAdd}><Plus />Добавить</button>
      </div>
      <div className={css.teaList}>
        {shown.map((t) => {
          /* f.n в key ряда: повторный флэш ремаунтит .flash и ✓ —
             анимации перезапускаются (эталон CFG:55-56). */
          const f = flash?.key === `tea:${t.id}` ? flash : null;
          return (
            <div
              key={f ? `${t.id}:f${f.n}` : t.id}
              className={`${css.tea} ${row?.id === t.id ? css.editing : ""} ${f ? css.flash : ""}`}
            >
              {row?.id === t.id ? editor : (
                <div
                  className={`${css.teaView} ${isPhantomTea(t.id) ? css.phantom : ""}`}
                  role="button"
                  tabIndex={isPhantomTea(t.id) ? -1 : 0}
                  onClick={() => openEdit(t)}
                  onKeyDown={(e) => { if (e.key === "Enter") openEdit(t); }}
                >
                  <span className={css.tn}>{t.name}</span>
                  <span className={`${css.price} ${t.price === 0 ? css.zero : ""}`}>{money(t.price)}</span>
                  <span className={css.tc}><Cap />{t.capacity}</span>
                  <button
                    type="button"
                    className={css.tx}
                    title="Удалить"
                    onClick={(e) => { e.stopPropagation(); requestDelete(t); }}
                  >
                    <Cross />
                  </button>
                </div>
              )}
              {f ? <span className={`${css.okm} ${css.rowOk}`}>✓</span> : null}
            </div>
          );
        })}
        {row?.id === null ? <div className={`${css.tea} ${css.adding}`}>{editor}</div> : null}
      </div>
      {shown.length === 0 && row?.id !== null ? (
        <div className={css.teaEmpty}>Нет чайных. Добавьте первую.</div>
      ) : null}
      <NumPad
        open={np !== null}
        label={`${np === "cap" ? "Вместимость" : "Цена"} · ${draft.name.trim() || "чайная"}`}
        mode={np === "cap" ? "int" : "money"}
        unit={np === "cap" ? "чел" : undefined}
        max={np === "cap" ? 30 : undefined}
        value={np === "cap" ? draft.capacity : draft.price}
        onCommit={(v) => setDraft((d) => (np === "cap" ? { ...d, capacity: v } : { ...d, price: v }))}
        onClose={() => setNp(null)}
      />
      {snack.element}
    </section>
  );
}
